import { useMemo } from 'react';
import { KlarnaAnalyticEvents } from './constants';
import { useKlarnaApproved } from './useKlarnaApproved';
import { RouteParams } from '../../../../packages/router';
import { useLinkTo, useOnce } from '../../hooks';
import analytics from '../analytics';
import { bookedWithKlarna } from '../UserAppointmentState';
import { cancelAppointment, UserAppointment } from '../../api/Users/Appointments';
import { DEFERRED_CLIENT_APPT_BOOKED_PARAMS_KEY } from '../consumer/Booking';
import nonCriticalException from '../exceptionLogger';
import { getItem as kvGetItem, removeItem as kvRemoveItem } from '../KeyValueStorage';
import trackNcdEvents from '../analytics/trackNcdEvents';
import { GuestClientAppointment } from '../../api/GuestClientAppointment';

export type UseKlarnaBookingResultParams = {
  appointment: GuestClientAppointment | UserAppointment,
  providerId: number,
  params: RouteParams,
};

/** Handles emitting events. If not approved due to denial or cancellation, it will redirect the
 * client back to the booking page.
 */
export function useKlarnaBookingResult(
  {
    appointment, providerId, params,
  }: UseKlarnaBookingResultParams,
): { loading: boolean } {
  const linkTo = useLinkTo({ replace: true });

  const isBookedWithKlarna = useMemo(() => (
    !!appointment && bookedWithKlarna(appointment)
  ), [appointment]);

  const {
    loading: isKlarnaLogicLoading,
    approved: isKlarnaApproved,
  } = useKlarnaApproved(params?.payment_intent_client_secret || '');

  useOnce(async () => {
    if (!isBookedWithKlarna) return;

    const trackingParams = {
      appointment_id: appointment.id,
      source: 'booking_success',
    };

    // We don't know if it's a denial or abandoned flow
    if (!isKlarnaApproved) {
      analytics.track(
        KlarnaAnalyticEvents.NotApproved,
        trackingParams,
      );

      await cancelAppointment(appointment.booked_by, appointment.id);

      linkTo('profile.book', {
        providerId,
        serviceId: appointment.time_blocks[0].service,
      });
      return;
    }

    analytics.track(
      KlarnaAnalyticEvents.Approved,
      trackingParams,
    );

    try {
      const {
        eventName,
        eventPayload,
        options,
      } = await kvGetItem(DEFERRED_CLIENT_APPT_BOOKED_PARAMS_KEY);
      if (eventPayload?.appt_id !== appointment.id) {
        throw new Error('Payload mismatch');
      }
      analytics.track(eventName, eventPayload, options);
      trackNcdEvents(eventName, eventPayload);
    } catch (err) {
      nonCriticalException(`Failed to emit deferred payload: ${err}`);
    }

    await kvRemoveItem(DEFERRED_CLIENT_APPT_BOOKED_PARAMS_KEY);
  }, (providerId > 0
    && isBookedWithKlarna
    && !isKlarnaLogicLoading
    && isKlarnaApproved !== null
  ) || null);

  return {
    loading: isKlarnaLogicLoading
      /* return {loading: true} also if appt is booked with Klarna but was not approved, since
         there is a redirect in this for un-approved Klarna appts */
      || (!isKlarnaLogicLoading && isBookedWithKlarna && isKlarnaApproved === false),
  };
}
